import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Campaign, User } from '../../types';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';
import { ALL_FILTER } from '../../constants/dashboardDefaults';
import { AUTO_REFRESH_OPTIONS } from '../../utils/coverageDashboardUtils';
import { getAttendanceStatusLabel } from '../../utils/attendanceLabels';

const ATTENDANCE_FILTER_OPTIONS = [
  'EXPECTED', 'PRESENT', 'SLIGHTLY_LATE', 'LATE', 'NO_CALL_NO_SHOW', 'SCHEDULED_OFF',
] as const;

interface DashboardFilterToolbarProps {
  date: string;
  onDateChange: (date: string) => void;
  campaignId: string;
  onCampaignChange: (id: string) => void;
  supervisorId: string;
  onSupervisorChange: (id: string) => void;
  attendanceStatus: string;
  onAttendanceChange: (status: string) => void;
  coverageStatus: string;
  onCoverageChange: (status: string) => void;
  autoRefreshMs: number;
  onAutoRefreshChange: (ms: number) => void;
  campaigns: Campaign[];
  supervisors: User[];
  onRefresh: () => void;
  isFetching: boolean;
  density: DashboardDensity;
  coverageScope?: 'assigned' | 'all_coverage';
  onCoverageScopeChange?: (scope: 'assigned' | 'all_coverage') => void;
  canUseCrossCampaignScope?: boolean;
}

const fieldSx = (compact: boolean) => ({
  minWidth: compact ? 88 : 120,
  flexShrink: compact ? 0 : undefined,
  '& .MuiInputBase-root': {
    height: compact ? 32 : 40,
    fontSize: compact ? '0.72rem' : '0.875rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: compact ? '0.68rem' : '0.8rem',
    transform: compact ? 'translate(10px, 7px) scale(1)' : undefined,
    '&.MuiInputLabel-shrink': {
      transform: compact ? 'translate(10px, -7px) scale(0.75)' : undefined,
    },
  },
});

const DashboardFilterToolbar: React.FC<DashboardFilterToolbarProps> = (props) => {
  const compact = isCompact(props.density);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: compact ? 'nowrap' : 'wrap',
        alignItems: 'center',
        gap: compact ? 0.5 : 1,
        px: compact ? 0.75 : 1.25,
        py: compact ? 0.25 : 0.75,
        mb: compact ? 0.5 : 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minHeight: compact ? 40 : 52,
        maxHeight: compact ? 40 : 60,
        overflowX: compact ? 'auto' : 'visible',
        '&::-webkit-scrollbar': { height: compact ? 4 : undefined },
      }}
    >
      <TextField
        type="date"
        size="small"
        label="Date"
        value={props.date}
        onChange={(e) => props.onDateChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ ...fieldSx(compact), width: compact ? 118 : 150 }}
      />
      <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 108 : 150 }}>
        <InputLabel shrink>Campaign</InputLabel>
        <Select
          value={props.campaignId || ALL_FILTER}
          label="Campaign"
          notched
          onChange={(e) => props.onCampaignChange(e.target.value)}
          renderValue={(selected) => {
            if (!selected || selected === ALL_FILTER) return 'All';
            return props.campaigns.find((c) => c.id === selected)?.name || 'All';
          }}
        >
          <MenuItem value={ALL_FILTER}>All Campaigns</MenuItem>
          {props.campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </Select>
      </FormControl>
      {props.canUseCrossCampaignScope && props.onCoverageScopeChange && (
        <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 130 : 170 }}>
          <InputLabel shrink>Coverage Scope</InputLabel>
          <Select
            value={props.coverageScope || 'assigned'}
            label="Coverage Scope"
            notched
            onChange={(e) => props.onCoverageScopeChange?.(e.target.value as 'assigned' | 'all_coverage')}
          >
            <MenuItem value="assigned">My Campaigns</MenuItem>
            <MenuItem value="all_coverage">All Coverage Needs</MenuItem>
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 120 : 140 }}>
        <InputLabel shrink>Supervisor</InputLabel>
        <Select
          value={props.supervisorId || ALL_FILTER}
          label="Supervisor"
          notched
          onChange={(e) => props.onSupervisorChange(e.target.value)}
          renderValue={(selected) => {
            if (!selected || selected === ALL_FILTER) return 'All';
            const supervisor = props.supervisors.find((s) => s.id === selected);
            return supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : 'All';
          }}
        >
          <MenuItem value={ALL_FILTER}>All Supervisors</MenuItem>
          {props.supervisors.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 110 : 130 }}>
        <InputLabel shrink>Attendance</InputLabel>
        <Select
          value={props.attendanceStatus}
          label="Attendance"
          notched
          onChange={(e) => props.onAttendanceChange(e.target.value)}
          renderValue={(selected) => (selected === ALL_FILTER ? 'All' : getAttendanceStatusLabel(selected))}
        >
          <MenuItem value={ALL_FILTER}>All</MenuItem>
          {ATTENDANCE_FILTER_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{getAttendanceStatusLabel(s)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 110 : 130 }}>
        <InputLabel shrink>Coverage</InputLabel>
        <Select
          value={props.coverageStatus}
          label="Coverage"
          notched
          onChange={(e) => props.onCoverageChange(e.target.value)}
          renderValue={(selected) => (selected === ALL_FILTER ? 'All' : selected.replace(/_/g, ' '))}
        >
          <MenuItem value={ALL_FILTER}>All</MenuItem>
          {['COVERED', 'PARTIALLY_COVERED', 'NOT_COVERED', 'NOT_REQUIRED'].map((s) => (
            <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ ...fieldSx(compact), minWidth: compact ? 100 : 120 }}>
        <InputLabel shrink>Refresh</InputLabel>
        <Select
          value={props.autoRefreshMs}
          label="Refresh"
          notched
          onChange={(e) => props.onAutoRefreshChange(Number(e.target.value))}
          renderValue={(selected) => AUTO_REFRESH_OPTIONS.find((o) => o.value === selected)?.label || '60 seconds'}
        >
          {AUTO_REFRESH_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title="Refresh data">
        <IconButton size="small" onClick={props.onRefresh} disabled={props.isFetching}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default DashboardFilterToolbar;
